import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Stage, Layer, Line, Rect, Circle, RegularPolygon, Star as KonvaStar, Transformer, Path, Text, Group, Ellipse } from 'react-konva';
import {
    Pencil, Undo2, Redo2, Minus, Trash2, Spline, Square, Triangle,
    Circle as CircleIcon, MousePointer2, Star, Hexagon, Pentagon,
    Magnet, PaintBucket, Palette, Pointer, Eraser, Download,
    Moon, Sun, Type, HelpCircle, Cylinder, Cone, Box, Pyramid, Globe
} from 'lucide-react';
import { generateFloodFill } from '../../utils/floodFill';

import ToolButton from './whiteboard/ToolButton';
import ColorButton from './whiteboard/ColorButton';
import Separator from './whiteboard/Separator';
import TextModal from './whiteboard/TextModal';
import StrokeWidthPicker from './whiteboard/StrokeWidthPicker';
import KeyboardShortcuts from './whiteboard/KeyboardShortcuts';
import { PREMIUM_COLORS, GRID_SIZE, STORAGE_KEY, TOOL_SHORTCUTS } from './whiteboard/constants';

const Whiteboard = () => {
    // Core State
    const [tool, setTool] = useState('pen');
    const [elements, setElements] = useState([]);
    const [color, setColor] = useState('#1a1a2e');
    const [strokeWidth, setStrokeWidth] = useState(6);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedNodeIndex, setSelectedNodeIndex] = useState(null);
    const [starPoints, setStarPoints] = useState(5);
    const [magnetMode, setMagnetMode] = useState(false);
    const [darkMode, setDarkMode] = useState(false);

    // History for Undo/Redo
    const [history, setHistory] = useState([[]]);
    const [historyIndex, setHistoryIndex] = useState(0);

    // Zoom & Pan
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);

    // Modals
    const [textModalOpen, setTextModalOpen] = useState(false);
    const [textModalPosition, setTextModalPosition] = useState({ x: 0, y: 0 });
    const [shortcutsOpen, setShortcutsOpen] = useState(false);

    // Refs
    const isDrawing = useRef(false);
    const [curvePhase, setCurvePhase] = useState('idle');
    const transformerRef = useRef();
    const stageRef = useRef();
    const containerRef = useRef();
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const lastPanPosition = useRef({ x: 0, y: 0 });

    // Computed
    const canUndo = historyIndex > 0;
    const canRedo = historyIndex < history.length - 1;

    // Load saved data on mount
    useEffect(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const data = JSON.parse(saved);
                if (data.elements) {
                    setElements(data.elements);
                    setHistory([data.elements]);
                }
                if (data.darkMode !== undefined) setDarkMode(data.darkMode);
                if (data.strokeWidth !== undefined) setStrokeWidth(data.strokeWidth);
            } catch (e) {
                console.error('Error loading whiteboard data:', e);
            }
        }
    }, []);

    // Auto-save on elements change
    useEffect(() => {
        const data = { elements, darkMode, strokeWidth };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }, [elements, darkMode, strokeWidth]);

    // Resize observer
    useEffect(() => {
        const observer = new ResizeObserver(entries => {
            for (let entry of entries) {
                setDimensions({
                    width: entry.contentRect.width,
                    height: entry.contentRect.height
                });
            }
        });

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => observer.disconnect();
    }, []);

    // Transformer effect
    useEffect(() => {
        if (selectedIds.length > 0 && transformerRef.current) {
            const stage = stageRef.current;
            const nodes = selectedIds
                .map(id => stage.findOne('#shape-' + id))
                .filter(node => node !== undefined);

            transformerRef.current.nodes(nodes);
            transformerRef.current.getLayer().batchDraw();
        } else if (transformerRef.current) {
            transformerRef.current.nodes([]);
        }
    }, [selectedIds, elements]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Skip if in modal or input
            if (textModalOpen || e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            // Undo: Ctrl+Z
            if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
                e.preventDefault();
                handleUndo();
            }
            // Redo: Ctrl+Y or Ctrl+Shift+Z
            if ((e.ctrlKey && e.key === 'y') || (e.ctrlKey && e.shiftKey && e.key === 'z')) {
                e.preventDefault();
                handleRedo();
            }
            // Save: Ctrl+S
            if (e.ctrlKey && e.key === 's') {
                e.preventDefault();
                handleExportPNG();
            }
            // Delete selection
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.length > 0) {
                e.preventDefault();
                handleDelete();
            }
            // Zoom shortcuts
            if (e.key === '+' || e.key === '=') {
                e.preventDefault();
                handleZoomIn();
            }
            if (e.key === '-') {
                e.preventDefault();
                handleZoomOut();
            }
            // Tool shortcuts
            const toolKey = e.key.toLowerCase();
            if (!e.ctrlKey && !e.altKey && TOOL_SHORTCUTS[toolKey]) {
                e.preventDefault();
                setToolAndReset(TOOL_SHORTCUTS[toolKey]);
            }
            // Panning with Space
            if (e.code === 'Space' && !isPanning) {
                e.preventDefault();
                setIsPanning(true);
            }
            // Help
            if (e.key === '?' || e.key === 'F1') {
                e.preventDefault();
                setShortcutsOpen(true);
            }
        };

        const handleKeyUp = (e) => {
            if (e.code === 'Space') {
                setIsPanning(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [historyIndex, history, isPanning, selectedIds, textModalOpen]);

    // Save to history
    const saveToHistory = useCallback((newElements) => {
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push([...newElements]);
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
    }, [history, historyIndex]);

    // Undo
    const handleUndo = useCallback(() => {
        if (historyIndex > 0) {
            const newIndex = historyIndex - 1;
            setHistoryIndex(newIndex);
            setElements([...history[newIndex]]);
            setSelectedIds([]);
            setSelectedNodeIndex(null);
            setCurvePhase('idle');
        }
    }, [historyIndex, history]);

    // Redo
    const handleRedo = useCallback(() => {
        if (historyIndex < history.length - 1) {
            const newIndex = historyIndex + 1;
            setHistoryIndex(newIndex);
            setElements([...history[newIndex]]);
            setSelectedIds([]);
        }
    }, [historyIndex, history]);

    // Export as PNG
    const handleExportPNG = () => {
        if (!stageRef.current) return;

        const uri = stageRef.current.toDataURL({ pixelRatio: 2 });
        const link = document.createElement('a');
        link.download = `pizarra-${Date.now()}.png`;
        link.href = uri;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Zoom handlers
    const handleZoomIn = () => setScale(Math.min(scale * 1.2, 5));
    const handleZoomOut = () => setScale(Math.max(scale / 1.2, 0.2));
    const handleResetView = () => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
    };

    // Wheel zoom (Ctrl + scroll)
    const handleWheel = (e) => {
        // Only zoom if Ctrl key is pressed
        if (!e.evt.ctrlKey) return;

        e.evt.preventDefault();
        const scaleBy = 1.1;
        const stage = stageRef.current;
        const oldScale = scale;

        const pointer = stage.getPointerPosition();
        const mousePointTo = {
            x: (pointer.x - position.x) / oldScale,
            y: (pointer.y - position.y) / oldScale,
        };

        const newScale = e.evt.deltaY < 0 ? oldScale * scaleBy : oldScale / scaleBy;
        const clampedScale = Math.max(0.2, Math.min(5, newScale));

        setScale(clampedScale);
        setPosition({
            x: pointer.x - mousePointTo.x * clampedScale,
            y: pointer.y - mousePointTo.y * clampedScale,
        });
    };

    // Layer manipulation
    const moveElementUp = () => {
        if (selectedIds.length === 0) return;
        const newElements = [...elements];
        selectedIds.forEach(id => {
            if (id < newElements.length - 1) {
                [newElements[id], newElements[id + 1]] = [newElements[id + 1], newElements[id]];
            }
        });
        setElements(newElements);
        setSelectedIds(selectedIds.map(id => Math.min(id + 1, elements.length - 1)));
        saveToHistory(newElements);
    };

    const moveElementDown = () => {
        if (selectedIds.length === 0) return;
        const newElements = [...elements];
        selectedIds.forEach(id => {
            if (id > 0) {
                [newElements[id], newElements[id - 1]] = [newElements[id - 1], newElements[id]];
            }
        });
        setElements(newElements);
        setSelectedIds(selectedIds.map(id => Math.max(id - 1, 0)));
        saveToHistory(newElements);
    };

    const moveElementToTop = () => {
        if (selectedIds.length === 0) return;
        const newElements = elements.filter((_, i) => !selectedIds.includes(i));
        const selectedElements = selectedIds.map(id => elements[id]);
        const result = [...newElements, ...selectedElements];
        setElements(result);
        setSelectedIds(selectedElements.map((_, i) => result.length - selectedElements.length + i));
        saveToHistory(result);
    };

    const moveElementToBottom = () => {
        if (selectedIds.length === 0) return;
        const newElements = elements.filter((_, i) => !selectedIds.includes(i));
        const selectedElements = selectedIds.map(id => elements[id]);
        const result = [...selectedElements, ...newElements];
        setElements(result);
        setSelectedIds(selectedElements.map((_, i) => i));
        saveToHistory(result);
    };

    // Handle stroke width change - applies to selection if any
    const handleStrokeWidthChange = (newWidth) => {
        setStrokeWidth(newWidth);

        // If there are selected elements, apply the new stroke width to them
        if (selectedIds.length > 0) {
            const newElements = [...elements];
            let hasChanges = false;

            selectedIds.forEach(id => {
                const el = newElements[id];
                // Only apply to elements that have strokeWidth (not text)
                if (el && el.tool !== 'text') {
                    newElements[id] = { ...el, strokeWidth: newWidth };
                    hasChanges = true;
                }
            });

            if (hasChanges) {
                setElements(newElements);
                saveToHistory(newElements);
            }
        }
    };
    const handleTextConfirm = ({ text, fontSize, fontFamily }) => {
        const newElement = {
            tool: 'text',
            text,
            fontSize,
            fontFamily,
            color,
            x: textModalPosition.x,
            y: textModalPosition.y,
            rotation: 0,
            scaleX: 1,
            scaleY: 1,
        };
        const newElements = [...elements, newElement];
        setElements(newElements);
        saveToHistory(newElements);
        setTextModalOpen(false);
    };

    const getSnappedPos = (pos) => {
        if (!magnetMode) return pos;

        const SNAP_DISTANCE = 20;
        let closestDist = Infinity;
        let snapPos = pos;

        elements.forEach(el => {
            if (['line', 'curve', 'pen'].includes(el.tool) && el.points) {
                const pointsToCheck = [
                    { x: el.points[0], y: el.points[1] },
                    { x: el.points[el.points.length - 2], y: el.points[el.points.length - 1] }
                ];

                pointsToCheck.forEach(pt => {
                    const dist = Math.sqrt(Math.pow(pt.x - pos.x, 2) + Math.pow(pt.y - pos.y, 2));
                    if (dist < SNAP_DISTANCE && dist < closestDist) {
                        closestDist = dist;
                        snapPos = pt;
                    }
                });
            } else if (el.x !== undefined && el.y !== undefined) {
                const dist = Math.sqrt(Math.pow(el.x - pos.x, 2) + Math.pow(el.y - pos.y, 2));
                if (dist < SNAP_DISTANCE && dist < closestDist) {
                    closestDist = dist;
                    snapPos = { x: el.x, y: el.y };
                }
            }
        });

        const gridX = Math.round(pos.x / GRID_SIZE) * GRID_SIZE;
        const gridY = Math.round(pos.y / GRID_SIZE) * GRID_SIZE;
        const gridDist = Math.sqrt(Math.pow(gridX - pos.x, 2) + Math.pow(gridY - pos.y, 2));

        if (gridDist < SNAP_DISTANCE && gridDist < closestDist) {
            snapPos = { x: gridX, y: gridY };
        }

        return snapPos;
    };

    const getTransformedPos = (stage) => {
        const pointer = stage.getPointerPosition();
        return {
            x: (pointer.x - position.x) / scale,
            y: (pointer.y - position.y) / scale,
        };
    };

    const handleMouseDown = (e) => {
        // Handle panning
        if (isPanning) {
            lastPanPosition.current = e.target.getStage().getPointerPosition();
            return;
        }

        // Handle text tool
        if (tool === 'text') {
            const pos = getTransformedPos(e.target.getStage());
            setTextModalPosition(pos);
            setTextModalOpen(true);
            return;
        }

        isDrawing.current = false;
        // These tools don't create new elements on mousedown
        // select/vertex: selection only
        // bucket: applies fill to existing elements (handled in handleElementClick)
        // side: creates segments on existing elements (handled in handleElementClick)
        if (['select', 'vertex', 'bucket', 'side'].includes(tool)) {
            if (e.target === e.target.getStage()) {
                setSelectedIds([]);
            }
            return;
        }

        let pos = getTransformedPos(e.target.getStage());

        if (['line', 'curve', 'pen'].includes(tool) || magnetMode) {
            pos = getSnappedPos(pos);
        }

        const baseElement = {
            tool,
            color,
            strokeWidth,
            rotation: 0,
            scaleX: 1,
            scaleY: 1,
            x: 0, y: 0,
        };

        if (tool === 'curve') {
            if (curvePhase === 'idle') {
                isDrawing.current = true;
                setCurvePhase('drawing_base');
                const newElements = [...elements, {
                    ...baseElement,
                    points: [pos.x, pos.y, pos.x, pos.y]
                }];
                setElements(newElements);
            } else if (curvePhase === 'bending') {
                isDrawing.current = true;
            }
            return;
        }

        isDrawing.current = true;
        setSelectedIds([]);

        let newElements;
        if (['pen', 'eraser', 'line'].includes(tool)) {
            newElements = [...elements, {
                ...baseElement,
                points: [pos.x, pos.y, (tool === 'line') ? pos.x : pos.x, (tool === 'line') ? pos.y : pos.y]
            }];
        } else if (tool === 'rect') {
            newElements = [...elements, {
                ...baseElement,
                x: pos.x, y: pos.y, width: 0, height: 0
            }];
        } else if (['circle', 'triangle', 'pentagon', 'hexagon', 'star', 'cylinder', 'cone', 'cubeprism', 'pyramid', 'sphere'].includes(tool)) {
            newElements = [...elements, {
                ...baseElement,
                x: pos.x, y: pos.y, radius: 0,
                rotation: 0,
                numPoints: (tool === 'star' || tool === 'pyramid') ? starPoints : undefined
            }];
        }

        if (newElements) setElements(newElements);
    };

    const handleMouseMove = (e) => {
        // Handle panning movement
        if (isPanning) {
            const stage = e.target.getStage();
            const pointer = stage.getPointerPosition();
            setPosition({
                x: position.x + (pointer.x - lastPanPosition.current.x),
                y: position.y + (pointer.y - lastPanPosition.current.y),
            });
            lastPanPosition.current = pointer;
            return;
        }

        if (tool === 'select' || tool === 'vertex' || tool === 'text') return;

        let info = getTransformedPos(e.target.getStage());

        if (['line', 'curve', 'pen'].includes(tool) && isDrawing.current) {
            info = getSnappedPos(info);
        }

        if (elements.length === 0) return;
        const lastEl = { ...elements[elements.length - 1] };

        if (tool === 'curve') {
            if (curvePhase === 'drawing_base') {
                if (!isDrawing.current) return;
                lastEl.points = [lastEl.points[0], lastEl.points[1], info.x, info.y];
            } else if (curvePhase === 'bending') {
                const startX = lastEl.points[0];
                const startY = lastEl.points[1];
                let endX, endY;
                if (lastEl.points.length === 4) {
                    endX = lastEl.points[2];
                    endY = lastEl.points[3];
                } else {
                    endX = lastEl.points[4];
                    endY = lastEl.points[5];
                }
                lastEl.points = [startX, startY, info.x, info.y, endX, endY];
            }
        }
        else if (tool === 'rect') {
            if (!isDrawing.current) return;
            if (lastEl.startX === undefined) { lastEl.startX = lastEl.x; lastEl.startY = lastEl.y; }
            const currentX = info.x;
            const currentY = info.y;
            lastEl.x = Math.min(lastEl.startX, currentX);
            lastEl.y = Math.min(lastEl.startY, currentY);
            lastEl.width = Math.abs(currentX - lastEl.startX);
            lastEl.height = Math.abs(currentY - lastEl.startY);

        } else if (['circle', 'triangle', 'pentagon', 'hexagon', 'star', 'cylinder', 'cone', 'cubeprism', 'pyramid', 'sphere'].includes(tool)) {
            if (!isDrawing.current) return;
            const dx = info.x - lastEl.x;
            const dy = info.y - lastEl.y;
            lastEl.radius = Math.sqrt(dx * dx + dy * dy);
        }
        else {
            if (!isDrawing.current) return;
            if (tool === 'line') {
                lastEl.points = [lastEl.points[0], lastEl.points[1], info.x, info.y];
            } else {
                lastEl.points = lastEl.points.concat([info.x, info.y]);
            }
        }

        const newElements = elements.slice(0, elements.length - 1).concat(lastEl);
        setElements(newElements);
    };

    const handleMouseUp = () => {
        if (isPanning) return;

        if (tool === 'curve') {
            if (curvePhase === 'drawing_base') {
                isDrawing.current = false;
                setCurvePhase('bending');
            } else if (curvePhase === 'bending' && isDrawing.current) {
                isDrawing.current = false;
                setCurvePhase('idle');
                saveToHistory(elements);
            }
            return;
        }

        if (isDrawing.current && (tool === 'line')) {
            const lastEl = elements[elements.length - 1];
            if (lastEl && lastEl.points && lastEl.points.length >= 4) {
                const dist = Math.sqrt(
                    Math.pow(lastEl.points[2] - lastEl.points[0], 2) +
                    Math.pow(lastEl.points[3] - lastEl.points[1], 2)
                );
                if (dist < 5) {
                    setElements(elements.slice(0, elements.length - 1));
                    isDrawing.current = false;
                    return;
                }
            }
        }

        if (isDrawing.current) {
            saveToHistory(elements);
        }

        isDrawing.current = false;
    };

    const handleTransformEnd = (i, newAttrs) => {
        const newElements = [...elements];
        newElements[i] = { ...newElements[i], ...newAttrs };
        setElements(newElements);
        saveToHistory(newElements);
    }

    const handleStageClick = (e) => {
        if (tool !== 'bucket') return;

        const stage = e.target.getStage();
        const pos = getTransformedPos(stage);
        const drawingLayer = stage.getLayers()[1];

        if (!drawingLayer) return;

        const points = generateFloodFill(stage, drawingLayer, pos.x, pos.y, color);

        if (points && points.length >= 6) {
            const newElements = [...elements, {
                tool: 'polygon',
                points: points,
                color: 'transparent',
                fill: color,
                x: 0, y: 0,
                rotation: 0,
                scaleX: 1, scaleY: 1
            }];
            setElements(newElements);
            saveToHistory(newElements);
        }
    };

    const handleObjectClick = (e, i) => {
        if (tool === 'select' || tool === 'vertex') {
            e.cancelBubble = true;
            const { shiftKey, ctrlKey } = e.evt || {};
            if (shiftKey || ctrlKey) {
                setSelectedIds(prev =>
                    prev.includes(i) ? prev.filter(id => id !== i) : [...prev, i]
                );
            } else {
                setSelectedIds([i]);
            }
        }
    };

    const handleDelete = () => {
        if (selectedIds.length > 0) {
            const newElements = elements.filter((_, i) => !selectedIds.includes(i));
            setElements(newElements);
            saveToHistory(newElements);
            setSelectedIds([]);
        }
    };

    const clearBoard = () => {
        if (window.confirm('¿Limpiar toda la pizarra?')) {
            setElements([]);
            saveToHistory([]);
            setSelectedIds([]);
        }
    };

    const handleNodeDrag = (elIndex, nodeIndex, newPos) => {
        setElements(prev => {
            const newElements = [...prev];
            const el = { ...newElements[elIndex] };

            if (['line', 'pen', 'curve', 'polygon', 'side'].includes(el.tool)) {
                const newPoints = [...el.points];
                const sX = el.scaleX || 1;
                const sY = el.scaleY || 1;
                // NewPos explicitly in local group coordinates (from e.target.x/y)
                // Rotation is handled by the parent Group
                // We just need to handle scale
                newPoints[nodeIndex * 2] = newPos.x / sX;
                newPoints[nodeIndex * 2 + 1] = newPos.y / sY;
                el.points = newPoints;
            }

            newElements[elIndex] = el;
            return newElements;
        });
    };

    const setToolAndReset = (newTool, overrideSelectedId = null) => {
        setTool(newTool);
        setCurvePhase('idle');
        isDrawing.current = false;
        const activeId = overrideSelectedId !== null ? overrideSelectedId : (selectedIds.length > 0 ? selectedIds[0] : null);

        if (newTool === 'vertex' && activeId !== null) {
            setElements(prev => {
                const newElements = [...prev];
                const el = { ...newElements[activeId] };
                if (!el) return prev;

                if (el.tool === 'rect') {
                    el.points = [0, 0, el.width, 0, el.width, el.height, 0, el.height];
                    el.tool = 'polygon';
                    delete el.width;
                    delete el.height;
                } else if (['triangle', 'pentagon', 'hexagon', 'star'].includes(el.tool)) {
                    let points = [];
                    if (el.tool === 'star') {
                        const count = (el.numPoints || 5) * 2;
                        for (let n = 0; n < count; n++) {
                            const r = n % 2 === 0 ? el.radius : el.radius / 2;
                            const angle = (n * 2 * Math.PI) / count - Math.PI / 2;
                            points.push(r * Math.cos(angle), r * Math.sin(angle));
                        }
                    } else {
                        const sides = el.tool === 'triangle' ? 3 : (el.tool === 'pentagon' ? 5 : 6);
                        const radius = el.radius || 50;
                        for (let n = 0; n < sides; n++) {
                            const angle = (n * 2 * Math.PI) / sides - Math.PI / 2;
                            points.push(radius * Math.cos(angle), radius * Math.sin(angle));
                        }
                    }
                    el.points = points;
                    el.tool = 'polygon';
                    delete el.radius;
                    delete el.numPoints;
                }

                if (el.points) {
                    const sX = el.scaleX || 1;
                    const sY = el.scaleY || 1;
                    if (sX !== 1 || sY !== 1) {
                        el.points = el.points.map((p, i) => i % 2 === 0 ? p * sX : p * sY);
                        el.scaleX = 1;
                        el.scaleY = 1;
                    }
                }
                newElements[activeId] = el;
                return newElements;
            });
        }

        if (newTool !== 'select' && newTool !== 'vertex') {
            setSelectedIds([]);
            setSelectedNodeIndex(null);
        }
    }

    // Grid rendering
    const renderGrid = () => {
        const width = dimensions.width / scale + Math.abs(position.x) / scale + GRID_SIZE;
        const height = dimensions.height / scale + Math.abs(position.y) / scale + GRID_SIZE;
        const lines = [];
        const gridColor = darkMode ? '#374151' : '#e2e8f0';
        const dotColor = darkMode ? '#4b5563' : '#cbd5e1';

        const startX = Math.floor(-position.x / scale / GRID_SIZE) * GRID_SIZE;
        const startY = Math.floor(-position.y / scale / GRID_SIZE) * GRID_SIZE;

        for (let i = startX; i < width + startX; i += GRID_SIZE) {
            lines.push(
                <Line
                    key={`v-${i}`}
                    points={[i, startY, i, height + startY]}
                    stroke={gridColor}
                    strokeWidth={1 / scale}
                />
            );
        }
        for (let j = startY; j < height + startY; j += GRID_SIZE) {
            lines.push(
                <Line
                    key={`h-${j}`}
                    points={[startX, j, width + startX, j]}
                    stroke={gridColor}
                    strokeWidth={1 / scale}
                />
            );
        }

        for (let i = startX; i <= width + startX; i += GRID_SIZE) {
            for (let j = startY; j <= height + startY; j += GRID_SIZE) {
                lines.push(
                    <Circle
                        key={`dot-${i}-${j}`}
                        x={i}
                        y={j}
                        radius={2 / scale}
                        fill={dotColor}
                        listening={false}
                    />
                );
            }
        }
        return lines;
    };

    // Render element based on tool type
    const renderElement = (el, i) => {
        const isEraser = el.tool === 'eraser';
        const isEditable = el.color !== 'transparent';
        const elStrokeWidth = el.strokeWidth || 6;

        const commonProps = {
            key: i,
            id: `shape-${i}`,
            stroke: isEraser ? '#000000' : (el.tool === 'polygon' && !isEditable ? undefined : el.color),
            strokeWidth: isEraser ? 40 : (el.tool === 'polygon' && !isEditable ? 0 : elStrokeWidth),
            hitStrokeWidth: 20,
            draggable: tool === 'select' || (tool === 'vertex' && el.tool === 'vertex'),
            x: el.x || 0,
            y: el.y || 0,
            rotation: el.rotation || 0,
            scaleX: el.scaleX || 1,
            scaleY: el.scaleY || 1,
            fill: el.fill || 'rgba(255,255,255,0.001)',
            globalCompositeOperation: isEraser ? 'destination-out' : 'source-over',
            onClick: (e) => handleElementClick(e, el, i),
            onTap: (e) => handleElementClick(e, el, i),
            onTransformEnd: (e) => {
                const node = e.target;
                handleTransformEnd(i, {
                    x: node.x(), y: node.y(),
                    rotation: node.rotation(),
                    scaleX: node.scaleX(), scaleY: node.scaleY()
                });
            },
            onDragEnd: (e) => {
                if (tool === 'select') {
                    const node = e.target;
                    const newElements = [...elements];
                    newElements[i] = {
                        ...newElements[i],
                        x: node.x(),
                        y: node.y()
                    };
                    setElements(newElements);
                    saveToHistory(newElements);
                }
            }
        };

        switch (el.tool) {
            case 'text':
                return (
                    <Text
                        key={i}
                        id={`shape-${i}`}
                        text={el.text}
                        fontSize={el.fontSize || 32}
                        fontFamily={el.fontFamily || 'Inter'}
                        fill={el.color}
                        x={el.x || 0}
                        y={el.y || 0}
                        rotation={el.rotation || 0}
                        scaleX={el.scaleX || 1}
                        scaleY={el.scaleY || 1}
                        draggable={tool === 'select'}
                        onClick={(e) => handleObjectClick(e, i)}
                        onTap={(e) => handleObjectClick(e, i)}
                        onTransformEnd={(e) => {
                            const node = e.target;
                            handleTransformEnd(i, {
                                x: node.x(), y: node.y(),
                                rotation: node.rotation(),
                                scaleX: node.scaleX(), scaleY: node.scaleY()
                            });
                        }}
                        onDragEnd={(e) => {
                            if (tool === 'select') {
                                const node = e.target;
                                const newElements = [...elements];
                                newElements[i] = { ...newElements[i], x: node.x(), y: node.y() };
                                setElements(newElements);
                                saveToHistory(newElements);
                            }
                        }}
                    />
                );
            case 'pen':
            case 'eraser':
            case 'line':
            case 'curve':
                return <Line {...commonProps} points={el.points} tension={tool === 'vertex' ? 0 : (['pen', 'curve'].includes(el.tool) ? 0.5 : 0)} lineCap="round" lineJoin="round" closed={!!el.fill} />;
            case 'rect':
                return <Rect {...commonProps} width={el.width} height={el.height} />;
            case 'circle':
                return <Circle {...commonProps} radius={el.radius} />;
            case 'triangle':
            case 'pentagon':
            case 'hexagon':
                return <RegularPolygon {...commonProps} sides={el.tool === 'triangle' ? 3 : (el.tool === 'pentagon' ? 5 : 6)} radius={el.radius} />;
            case 'star':
                return <KonvaStar {...commonProps} numPoints={el.numPoints || 5} innerRadius={el.radius / 2} outerRadius={el.radius} />;
            case 'polygon':
                return <Line {...commonProps} points={el.points} closed={true} />;
            case 'side':
                return <Line {...commonProps} points={el.points} strokeWidth={10} />;
            case 'path':
                return <Path {...commonProps} data={el.data} />;
            default:
                return null;
        }
    };

    const handleElementClick = (e, el, i) => {
        handleObjectClick(e, i);
        if (tool === 'bucket') {
            e.cancelBubble = true;
            const newElements = [...elements];
            newElements[i] = { ...newElements[i], fill: color };
            setElements(newElements);
            saveToHistory(newElements);
        }
        if (tool === 'side') {
            const stage = e.target.getStage();
            const pointerPos = stage.getPointerPosition();
            const node = e.target;
            let rawPoints = [];
            if (el.tool === 'rect') rawPoints = [0, 0, el.width, 0, el.width, el.height, 0, el.height, 0, 0];
            else if (['triangle', 'pentagon', 'hexagon'].includes(el.tool)) {
                const sides = el.tool === 'triangle' ? 3 : (el.tool === 'pentagon' ? 5 : 6);
                const r = el.radius;
                for (let n = 0; n <= sides; n++) {
                    const a = (n * 2 * Math.PI) / sides - Math.PI / 2;
                    rawPoints.push(r * Math.cos(a), r * Math.sin(a));
                }
            } else if (el.tool === 'star') {
                const sides = (el.numPoints || 5) * 2;
                for (let n = 0; n <= sides; n++) {
                    const r = n % 2 === 0 ? el.radius : el.radius / 2;
                    const a = (n * 2 * Math.PI) / sides - Math.PI / 2;
                    rawPoints.push(r * Math.cos(a), r * Math.sin(a));
                }
            } else if (['line', 'pen', 'curve', 'polygon'].includes(el.tool)) {
                rawPoints = [...el.points];
                if (el.tool === 'polygon') rawPoints.push(rawPoints[0], rawPoints[1]);
            }

            if (rawPoints.length >= 4) {
                const transform = node.getAbsoluteTransform();
                const transformedPoints = [];
                for (let n = 0; n < rawPoints.length; n += 2) {
                    const pt = transform.point({ x: rawPoints[n], y: rawPoints[n + 1] });
                    transformedPoints.push(pt.x, pt.y);
                }
                let minTargetDist = Infinity;
                let nearestSegment = [];
                for (let n = 0; n < transformedPoints.length - 2; n += 2) {
                    const x1 = transformedPoints[n], y1 = transformedPoints[n + 1];
                    const x2 = transformedPoints[n + 2], y2 = transformedPoints[n + 3];
                    const A = pointerPos.x - x1, B = pointerPos.y - y1;
                    const C = x2 - x1, D = y2 - y1;
                    const dot = A * C + B * D;
                    const lenSq = C * C + D * D;
                    let param = -1;
                    if (lenSq !== 0) param = dot / lenSq;
                    let xx, yy;
                    if (param < 0) { xx = x1; yy = y1; }
                    else if (param > 1) { xx = x2; yy = y2; }
                    else { xx = x1 + param * C; yy = y1 + param * D; }
                    const d = Math.sqrt(Math.pow(pointerPos.x - xx, 2) + Math.pow(pointerPos.y - yy, 2));
                    if (d < minTargetDist) { minTargetDist = d; nearestSegment = [x1, y1, x2, y2]; }
                }
                if (nearestSegment.length === 4) {
                    const newElements = [...elements, { tool: 'side', points: nearestSegment, color: color, x: 0, y: 0, rotation: 0, scaleX: 1, scaleY: 1 }];
                    setElements(newElements);
                    saveToHistory(newElements);
                }
            }
        }
        if (tool === 'vertex') {
            setToolAndReset('vertex', i);
        }
    };

    return (
        <div className={`flex flex-row h-full rounded-3xl shadow-2xl overflow-hidden border-2 transition-colors duration-500 ${darkMode
            ? 'bg-gray-900 border-violet-500/30'
            : 'bg-slate-50 border-blue-200/50'
            }`}>
            {/* Premium Sidebar */}
            <div className={`w-40 md:w-48 flex flex-col h-full z-20 transition-colors duration-500 ${darkMode
                ? 'bg-gray-800/90 backdrop-blur-xl border-r border-white/10'
                : 'bg-white/80 backdrop-blur-xl border-r border-gray-200/50'
                }`}>
                {/* Header - Compact */}
                <div className={`px-3 py-2 flex items-center justify-center gap-2 transition-colors duration-500 ${darkMode
                    ? 'bg-gradient-to-r from-violet-600 to-indigo-800'
                    : 'bg-gradient-to-r from-blue-500 to-indigo-600'
                    }`}>
                    <Palette size={20} className="text-white drop-shadow-lg" strokeWidth={2} />
                    <span className="font-bold text-white text-xs uppercase tracking-widest drop-shadow">Pizarra</span>
                </div>

                {/* Premium Color Palette - Right below header */}
                <div className={`px-3 py-2 transition-colors duration-500 ${darkMode ? 'bg-gray-800/50' : 'bg-gray-50/80'}`}>
                    <div className="grid grid-cols-8 gap-1">
                        {PREMIUM_COLORS.map((c) => (
                            <ColorButton
                                key={c}
                                color={c}
                                selected={color === c}
                                onClick={() => setColor(c)}
                                darkMode={darkMode}
                            />
                        ))}
                    </div>
                </div>

                {/* Tools Container */}
                <div className="flex-1 overflow-y-auto p-3 space-y-3 scrollbar-hide">
                    {/* Main Tools */}
                    <div className="grid grid-cols-2 gap-1.5">
                        <ToolButton darkMode={darkMode} mini label="Selecc" icon={MousePointer2} active={tool === 'select'} onClick={() => setToolAndReset('select')} />
                        <ToolButton darkMode={darkMode} mini label="Lápiz" icon={Pencil} active={tool === 'pen'} onClick={() => setToolAndReset('pen')} />
                        <ToolButton darkMode={darkMode} mini label="Recta" icon={Minus} active={tool === 'line'} onClick={() => setToolAndReset('line')} />
                        <ToolButton darkMode={darkMode} mini label="Curva" icon={Spline} active={tool === 'curve'} onClick={() => setToolAndReset('curve')} />
                        <ToolButton darkMode={darkMode} mini label="Relleno" icon={PaintBucket} active={tool === 'bucket'} onClick={() => setToolAndReset('bucket')} />
                        <ToolButton darkMode={darkMode} mini label="Texto" icon={Type} active={tool === 'text'} onClick={() => setToolAndReset('text')} />
                        <ToolButton darkMode={darkMode} mini label="Vértice" icon={Pointer} active={tool === 'vertex'} onClick={() => setToolAndReset('vertex')} />
                        <ToolButton darkMode={darkMode} mini label="Borrador" icon={Eraser} active={tool === 'eraser'} onClick={() => setToolAndReset('eraser')} />
                    </div>

                    <Separator darkMode={darkMode} />

                    <Separator darkMode={darkMode} />

                    <Separator darkMode={darkMode} />

                    {/* Shapes */}
                    <div className="grid grid-cols-2 gap-1.5">
                        <ToolButton darkMode={darkMode} mini label="Cuad" icon={Square} active={tool === 'rect'} onClick={() => setToolAndReset('rect')} />
                        <ToolButton darkMode={darkMode} mini label="Círc" icon={CircleIcon} active={tool === 'circle'} onClick={() => setToolAndReset('circle')} />
                        <ToolButton darkMode={darkMode} mini label="Trián" icon={Triangle} active={tool === 'triangle'} onClick={() => setToolAndReset('triangle')} />
                        <ToolButton darkMode={darkMode} mini label="Pent" icon={Pentagon} active={tool === 'pentagon'} onClick={() => setToolAndReset('pentagon')} />
                        <ToolButton darkMode={darkMode} mini label="Hex" icon={Hexagon} active={tool === 'hexagon'} onClick={() => setToolAndReset('hexagon')} />
                        <ToolButton darkMode={darkMode} mini label="Estr" icon={Star} active={tool === 'star'} onClick={() => setToolAndReset('star')} />
                    </div>



                    {/* Stroke Width */}
                    <StrokeWidthPicker
                        value={selectedIds.length > 0 && elements[selectedIds[0]]?.strokeWidth
                            ? elements[selectedIds[0]].strokeWidth
                            : strokeWidth
                        }
                        onChange={handleStrokeWidthChange}
                        darkMode={darkMode}
                        hasSelection={selectedIds.length > 0 && elements[selectedIds[0]]?.tool !== 'text'}
                        selectionLabel={selectedIds.length > 0 ? `Editando ${selectedIds.length} elemento${selectedIds.length > 1 ? 's' : ''}` : null}
                    />

                    <Separator darkMode={darkMode} />

                    {/* History */}
                    <div className="grid grid-cols-2 gap-1.5">
                        <ToolButton darkMode={darkMode} mini label="Deshacer" icon={Undo2} onClick={handleUndo} disabled={!canUndo} />
                        <ToolButton darkMode={darkMode} mini label="Rehacer" icon={Redo2} onClick={handleRedo} disabled={!canRedo} />
                    </div>

                    <Separator darkMode={darkMode} />

                    {/* Actions */}
                    <div className="grid grid-cols-2 gap-1.5">
                        <button
                            onClick={() => setMagnetMode(!magnetMode)}
                            className={`p-2 rounded-2xl border-2 transition-all duration-300 flex flex-col items-center justify-center gap-1 ${magnetMode
                                ? darkMode
                                    ? 'bg-violet-600 border-violet-400 text-white shadow-lg shadow-violet-500/30'
                                    : 'bg-blue-500 border-blue-400 text-white shadow-lg shadow-blue-500/30'
                                : darkMode
                                    ? 'bg-white/5 border-white/10 text-gray-500'
                                    : 'bg-gray-50 border-gray-200 text-gray-400'
                                }`}
                        >
                            <Magnet size={18} />
                            <span className="text-[9px] font-semibold uppercase">Imán</span>
                        </button>
                        <button
                            onClick={() => setDarkMode(!darkMode)}
                            className={`p-2 rounded-2xl border-2 transition-all duration-300 flex flex-col items-center justify-center gap-1 ${darkMode
                                ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400'
                                : 'bg-indigo-50 border-indigo-200 text-indigo-500'
                                }`}
                        >
                            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
                            <span className="text-[9px] font-semibold uppercase">{darkMode ? 'Claro' : 'Oscuro'}</span>
                        </button>
                    </div>

                    <div className="grid grid-cols-2 gap-1.5">
                        <ToolButton darkMode={darkMode} mini label="Guardar" icon={Download} onClick={handleExportPNG} />
                        <ToolButton darkMode={darkMode} mini label="Ayuda" icon={HelpCircle} onClick={() => setShortcutsOpen(true)} />
                    </div>

                    <div className="grid grid-cols-1 gap-1.5">
                        <ToolButton darkMode={darkMode} label="Limpiar Todo" icon={Trash2} onClick={clearBoard} danger />
                    </div>

                    {/* Delete selected */}
                    {selectedIds.length > 0 && (
                        <button
                            onClick={handleDelete}
                            className={`w-full py-2.5 rounded-2xl flex items-center justify-center gap-2 transition-all duration-300 ${darkMode
                                ? 'bg-red-900/50 text-red-400 border border-red-500/50 hover:bg-red-800'
                                : 'bg-red-50 text-red-500 border border-red-200 hover:bg-red-500 hover:text-white'
                                }`}
                        >
                            <Trash2 size={16} strokeWidth={2.5} />
                            <span className="text-[11px] font-bold uppercase">Borrar ({selectedIds.length})</span>
                        </button>
                    )}
                </div>

                {/* Star/Pyramid Points Popover */}
                {(['star', 'pyramid'].includes(tool) || (tool === 'select' && selectedIds.length === 1 && ['star', 'pyramid'].includes(elements[selectedIds[0]]?.tool))) && (
                    <div className="absolute left-40 md:left-48 top-1/2 -translate-y-1/2 ml-4 z-50">
                        <div className={`p-4 rounded-3xl shadow-2xl flex flex-col items-center gap-3 backdrop-blur-xl border ${darkMode
                            ? 'bg-gray-800/90 border-violet-500/50'
                            : 'bg-white/90 border-blue-200'
                            }`}>
                            <span className={`font-bold text-xs uppercase ${darkMode ? 'text-violet-400' : 'text-blue-600'}`}>
                                {(tool === 'star' || (tool === 'select' && elements[selectedIds[0]]?.tool === 'star')) ? 'Puntas' : 'Lados Base'}
                            </span>
                            <div className={`px-4 py-2 rounded-xl font-bold text-2xl ${darkMode
                                ? 'bg-violet-600/30 text-violet-300'
                                : 'bg-blue-100 text-blue-600'
                                }`}>
                                {(tool === 'select' && selectedIds.length === 1) ? (elements[selectedIds[0]].numPoints || 5) : starPoints}
                            </div>
                            <input
                                type="range"
                                min="3"
                                max="12"
                                value={(tool === 'select' && selectedIds.length === 1) ? (elements[selectedIds[0]].numPoints || 5) : starPoints}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (tool === 'select' && selectedIds.length === 1) {
                                        const newElements = [...elements];
                                        newElements[selectedIds[0]] = { ...newElements[selectedIds[0]], numPoints: val };
                                        setElements(newElements);
                                        saveToHistory(newElements);
                                    } else {
                                        setStarPoints(val);
                                    }
                                }}
                                className={`w-28 h-2 rounded-lg appearance-none cursor-pointer ${darkMode ? 'accent-violet-500' : 'accent-blue-500'}`}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Canvas Area */}
            <div
                ref={containerRef}
                className={`flex-1 relative overflow-hidden transition-colors duration-500 ${isPanning ? 'cursor-grab active:cursor-grabbing' : tool === 'text' ? 'cursor-text' : 'cursor-crosshair'
                    } ${darkMode ? 'bg-gray-900' : 'bg-white'}`}
            >
                <Stage
                    ref={stageRef}
                    width={dimensions.width}
                    height={dimensions.height}
                    scaleX={scale}
                    scaleY={scale}
                    x={position.x}
                    y={position.y}
                    onMouseDown={handleMouseDown}
                    onMousemove={handleMouseMove}
                    onMouseup={handleMouseUp}
                    onWheel={handleWheel}
                    onClick={handleStageClick}
                    onTouchStart={handleMouseDown}
                    onTouchMove={handleMouseMove}
                    onTouchEnd={handleMouseUp}
                >
                    {/* Grid Layer */}
                    <Layer listening={false}>
                        {renderGrid()}
                    </Layer>

                    {/* Drawing Layer */}
                    <Layer>
                        {elements.map((el, i) => renderElement(el, i))}

                        {/* Vertex editing nodes */}
                        {tool === 'vertex' && selectedIds.length > 0 && elements[selectedIds[0]] && (
                            ['line', 'pen', 'curve', 'polygon', 'side'].includes(elements[selectedIds[0]].tool) ? (
                                <Group
                                    x={elements[selectedIds[0]].x || 0}
                                    y={elements[selectedIds[0]].y || 0}
                                    rotation={elements[selectedIds[0]].rotation || 0}
                                >
                                    {elements[selectedIds[0]].points.map((_, idx) => {
                                        if (idx % 2 !== 0) return null;
                                        const el = elements[selectedIds[0]];
                                        // Local coordinates scaled (Group handles rotation and translation)
                                        const x = el.points[idx] * (el.scaleX || 1);
                                        const y = el.points[idx + 1] * (el.scaleY || 1);

                                        return (
                                            <Circle
                                                key={`node-${idx}`}
                                                x={x}
                                                y={y}
                                                radius={10 / scale}
                                                fill={darkMode ? '#a78bfa' : '#3b82f6'}
                                                stroke={darkMode ? '#7c3aed' : '#1d4ed8'}
                                                strokeWidth={3 / scale}
                                                shadowBlur={8}
                                                shadowColor={darkMode ? '#8b5cf6' : '#3b82f6'}
                                                shadowOpacity={0.5}
                                                draggable
                                                onClick={(e) => { e.cancelBubble = true; }}
                                                onMouseDown={(e) => { e.cancelBubble = true; isDrawing.current = false; }}
                                                onTap={(e) => { e.cancelBubble = true; }}
                                                onDragMove={(e) => {
                                                    const node = e.target;
                                                    if (magnetMode) {
                                                        const abs = node.getAbsolutePosition();
                                                        const snapped = getSnappedPos(abs);
                                                        node.setAbsolutePosition(snapped);
                                                    }
                                                    handleNodeDrag(selectedIds[0], idx / 2, { x: node.x(), y: node.y() });
                                                }}
                                                onDragEnd={() => saveToHistory(elements)}
                                                onMouseEnter={(e) => {
                                                    e.target.getStage().container().style.cursor = 'move';
                                                    e.target.scale({ x: 1.3, y: 1.3 });
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.getStage().container().style.cursor = 'crosshair';
                                                    e.target.scale({ x: 1, y: 1 });
                                                }}
                                            />
                                        );
                                    })}
                                </Group>
                            ) : null
                        )}

                        <Transformer
                            ref={transformerRef}
                            visible={tool === 'select'}
                            anchorSize={20 / scale}
                            anchorCornerRadius={10}
                            borderStrokeWidth={2 / scale}
                            borderStroke={darkMode ? '#a78bfa' : '#3b82f6'}
                            anchorStroke={darkMode ? '#7c3aed' : '#1d4ed8'}
                            anchorFill={darkMode ? '#c4b5fd' : '#93c5fd'}
                            borderDash={[8, 4]}
                            padding={8}
                            boundBoxFunc={(oldBox, newBox) => {
                                if (newBox.width < 5 || newBox.height < 5) return oldBox;
                                return newBox;
                            }}
                        />
                    </Layer>
                </Stage>

                {/* Status Bar */}
                <div className={`absolute bottom-4 right-4 flex gap-2`}>
                    <div className={`px-3 py-1.5 rounded-full text-xs font-bold backdrop-blur-md transition-colors duration-500 ${darkMode
                        ? 'bg-gray-800/80 text-gray-300 border border-white/10'
                        : 'bg-white/80 text-gray-600 border border-gray-200'
                        }`}>
                        {Math.round(scale * 100)}%
                    </div>
                    <div className={`px-3 py-1.5 rounded-full text-xs backdrop-blur-md transition-colors duration-500 ${darkMode
                        ? 'bg-gray-800/80 text-gray-400 border border-white/10'
                        : 'bg-white/80 text-gray-500 border border-gray-200'
                        }`}>
                        <kbd className="font-mono">?</kbd> Ayuda
                    </div>
                </div>
            </div>

            {/* Modals */}
            <TextModal
                isOpen={textModalOpen}
                onClose={() => setTextModalOpen(false)}
                onConfirm={handleTextConfirm}
                darkMode={darkMode}
            />

            <KeyboardShortcuts
                isOpen={shortcutsOpen}
                onClose={() => setShortcutsOpen(false)}
                darkMode={darkMode}
            />
        </div>
    );
};

export default Whiteboard;
